import React from 'react';
import styled from 'styled-components';
import { semanticColors } from '@/common/styles/colorTokens';
import { typography } from '@/common/styles/typographyTokens';

export interface ViewOption {
  icon?: React.ReactNode;
  label: string;
}

export type ViewSwitcherSize = 'sm' | 'lg';

interface ViewSwitcherProps {
  options: ViewOption[];
  selectedIndex: number;
  onChange: (index: number) => void;
  className?: string;
  size?: ViewSwitcherSize;
  id?: string;
}

/**
 * 뷰 전환을 위한 스위처 컴포넌트
 * 외부에서 전달된 옵션으로 버튼을 구성하고 선택된 인덱스를 관리합니다.
 */
const ViewSwitcher: React.FC<ViewSwitcherProps> = ({
  options,
  selectedIndex,
  onChange,
  className,
  size = 'sm',
  id,
}) => {
  if (!options || options.length === 0) {
    return null;
  }

  return (
    <Container id={id} className={className}>
      {options.map((option, index) => (
        <SwitchButton 
          key={`view-option-${index}`}
          $isActive={selectedIndex === index} 
          $size={size}
          onClick={() => onChange(index)}
          type="button"
        >
          {option.icon && <IconWrapper>{option.icon}</IconWrapper>}
          {option.label}
        </SwitchButton>
      ))}
    </Container>
  );
};

const Container = styled.div`
  width: max-content;
  display: flex;
  border-radius: 8px;
  overflow: hidden;
  background-color: ${semanticColors.background.secondary};
  border: 1px solid ${semanticColors.border.tertiary};
`;

const SwitchButton = styled.button<{ $isActive: boolean; $size: ViewSwitcherSize }>`
  display: flex;
  align-items: center;
  justify-content: center;
  padding: ${props => props.$size === 'sm' ? '8px 16px' : '12px 20px'};
  ${props => props.$size === "sm" ? typography.button.button4 : typography.button.button3}
  font-weight: 500;
  gap: 8px;
  cursor: pointer;
  border: none;
  background-color: ${props => props.$isActive ? semanticColors.background.primaryHover : 'transparent'};
  color: ${semanticColors.text.secondary};
  transition: all 0.2s ease-in-out;
  
  &:hover {
    background-color: ${props => props.$isActive 
      ? semanticColors.background.primary 
      : semanticColors.background.secondaryHover};
  }
  
  &:focus {
    outline: none;
    box-shadow: ${props => props.$isActive 
      ? `0 0 0 2px ${semanticColors.border.primary}`
      : 'none'};
  }
`;

const IconWrapper = styled.span`
  display: flex;
  align-items: center;
  justify-content: center;
`;

// 기본 아이콘 (이전 버전과의 호환성 및 사용 편의성을 위해 제공)
export const CardIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="currentColor" viewBox="0 0 16 16">
    <path d="M4 4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2H4V4zm2-1a1 1 0 0 0-1 1v1h6V4a1 1 0 0 0-1-1H6zm5 5H5a1 1 0 0 0-1 1v4a1 1 0 0 0 1 1h6a1 1 0 0 0 1-1v-4a1 1 0 0 0-1-1z"/>
  </svg>
);

export const ListIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="currentColor" viewBox="0 0 16 16">
    <path fillRule="evenodd" d="M2 2.5a.5.5 0 0 0-.5.5v11a.5.5 0 0 0 .5.5h12a.5.5 0 0 0 .5-.5V3a.5.5 0 0 0-.5-.5H2zM1 3a1 1 0 0 1 1-1h12a1 1 0 0 1 1 1v11a1 1 0 0 1-1 1H2a1 1 0 0 1-1-1V3zm2 1a.5.5 0 0 0 0 1h10a.5.5 0 0 0 0-1H3zm0 3a.5.5 0 0 0 0 1h10a.5.5 0 0 0 0-1H3zm0 3a.5.5 0 0 0 0 1h10a.5.5 0 0 0 0-1H3z"/>
  </svg>
);

export default ViewSwitcher; 